import type { AdminSelf } from '@saay/api-client';
import { buttonVariants } from '@saay/shadcn/components/button';
import { formatCount, formatTimestamp, formatToman } from '@saay/formatting';
import {
  Banner,
  Button,
  DetailGrid,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  LoadMore,
  Ltr,
  Masked,
  Menu,
  MenuItem,
  SheetSection,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
  useWindowClass,
} from '../kit.js';
import { useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router';

import type { EnrollmentRequest, Organization, OrganizationMembership } from '../api.js';
import { formatProgramCodeRevision } from '../display.js';
import { creditProgramTargetingPath } from '../nav.js';
import { DetailHeader } from '../components/detail-header.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { OPENED_FROM_LIST, onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { canSeeArea, canUse } from '../permissions.js';
import {
  listStatus,
  useActiveOrganizationRoles,
  useEnrollmentRequests,
  useOrganization,
  useOrganizationMemberships,
} from '../queries.js';
import {
  MembershipCreateDialog,
  MembershipUpdateDialog,
  OrganizationDialog,
  organizationTypeLabel,
} from './organization-forms.js';
import type { OrganizationsOutletContext } from './organizations.js';
import { ScopeMarketingAssignments } from './marketing-assignments.js';

/**
 * `/organizations/:organizationId`: the prototype organization drawer (`organizationDetailSheet`) with
 * «نمای کلی», «اعضا», «نقش و اعتبار», «ثبت‌نام گروهی» (O3–O7, O10), and «بازاریابی» (AC §28).
 */

type DetailTab = 'members' | 'credit' | 'enrollments' | 'marketing';

function enrollmentOutcomeText(counts: EnrollmentRequest['countsByRowStatus']): string {
  return `${formatCount(counts.PROVISIONED)} حساب جدید · ${formatCount(counts.LINKED_EXISTING_BUYER)} متصل`;
}

function OrganizationHeaderActions({
  onAddMember,
  onEdit,
  organization,
  self,
}: {
  readonly onAddMember: () => void;
  readonly onEdit: () => void;
  readonly organization: Organization;
  readonly self: AdminSelf;
}) {
  const manage = canUse(self, 'updateOrganization');
  const canEnroll = organization.status === 'ACTIVE' && canUse(self, 'createEnrollmentRequest');
  const canAdd = organization.status === 'ACTIVE' && canUse(self, 'createOrganizationMembership');
  const compactHeadline = useWindowClass('expanded') === 'compact';
  if (!manage && !canEnroll && !canAdd) {
    return null;
  }
  const enrollPath = `/organizations/${organization.id}/enrollments/new`;
  const enrollAction = canEnroll ? (
    <Link className={buttonVariants()} state={OPENED_FROM_LIST} to={enrollPath}>
      ثبت‌نام گروهی
    </Link>
  ) : null;
  if (compactHeadline) {
    const hasOverflow = canAdd || manage;
    return (
      <>
        {enrollAction}
        {hasOverflow ? (
          <Menu
            align="end"
            label="عملیات سازمان"
            trigger={<IconButton icon="more" label="عملیات بیشتر" type="button" variant="tonal" />}
          >
            {canAdd ? <MenuItem onSelect={onAddMember}>افزودن عضو</MenuItem> : null}
            {manage ? <MenuItem onSelect={onEdit}>ویرایش سازمان</MenuItem> : null}
          </Menu>
        ) : null}
      </>
    );
  }
  return (
    <>
      {enrollAction}
      {canAdd ? (
        <Button onClick={onAddMember} size="sm" variant="secondary">
          <Icon name="plus" />
          افزودن عضو
        </Button>
      ) : null}
      {manage ? (
        <Menu
          align="end"
          label="عملیات سازمان"
          trigger={<IconButton icon="more" label="عملیات بیشتر" type="button" variant="tonal" />}
        >
          <MenuItem onSelect={onEdit}>ویرایش سازمان</MenuItem>
        </Menu>
      ) : null}
    </>
  );
}

function Members({
  onAddMember,
  organization,
  self,
}: {
  readonly onAddMember: () => void;
  readonly organization: Organization;
  readonly self: AdminSelf;
}) {
  const [roleId, setRoleId] = useState('');
  const roles = useActiveOrganizationRoles();
  const memberships = useOrganizationMemberships(organization.id, {
    ...(roleId === '' ? {} : { organizationRoleId: roleId }),
    status: 'ACTIVE',
  });
  const rows = memberships.data?.pages.flatMap((page) => page.items) ?? [];
  const [editing, setEditing] = useState<OrganizationMembership | null>(null);
  const canAdd = organization.status === 'ACTIVE' && canUse(self, 'createOrganizationMembership');
  const canEdit = canUse(self, 'updateOrganizationMembership');
  // A role without the buyers area (Marketing Admin) gets the name, not a link to «به این بخش دسترسی ندارید».
  const opensBuyers = canSeeArea(self, 'buyers');
  return (
    <SheetSection
      actions={
        canAdd ? (
          <Button onClick={onAddMember} size="sm" variant="ghost">
            <Icon name="plus" />
            عضو جدید
          </Button>
        ) : undefined
      }
      id="organization-members"
      title="اعضا"
    >
      <FilterPillBar
        activeFilterCount={roleId === '' ? 0 : 1}
        label="فیلتر اعضای سازمان"
        onClearAll={() => setRoleId('')}
      >
        <FilterPill
          emptyLabel="همه نقش‌ها"
          label="نقش سازمانی"
          onChange={setRoleId}
          options={(roles.data ?? []).map((role) => ({ label: role.nameFa, value: role.id }))}
          value={roleId}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState icon="users" title="عضوی با این فیلترها نیست" />}
        error={memberships.error}
        isEmpty={rows.length === 0}
        onRetry={() => void memberships.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری اعضا…" lines={4} />}
        status={listStatus(memberships)}
      >
        <Table
          caption="اعضای سازمان"
          columns={[
            {
              header: 'عضو',
              key: 'buyer',
              pin: 'start',
              render: (row) => (
                <span className="saay-cell-main">
                  {opensBuyers ? (
                    <Link to={`/buyers/${row.buyer.buyerId}`}>{row.buyer.displayNameFa}</Link>
                  ) : (
                    <strong>{row.buyer.displayNameFa}</strong>
                  )}
                  <small>
                    <Masked kind="nationalCode" value={row.buyer.nationalCodeMasked} />
                  </small>
                </span>
              ),
            },
            {
              header: 'نقش',
              key: 'role',
              render: (row) => row.role.nameFa,
            },
            { header: 'عنوان محلی', key: 'title', render: (row) => row.localTitleFa ?? '—' },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => <StatusChip enumName="activeStatus" value={row.status} />,
            },
            {
              detail: false,
              header: <span className="saay-visually-hidden">ویرایش</span>,
              key: 'edit',
              pin: 'end',
              render: (row) =>
                canEdit && row.status === 'ACTIVE' ? (
                  <Button onClick={() => setEditing(row)} size="sm" variant="ghost">
                    ویرایش
                  </Button>
                ) : null,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {memberships.isFetchNextPageError ? (
          <ErrorState error={memberships.error} onRetry={() => void memberships.fetchNextPage()} />
        ) : null}
        <LoadMore
          hasMore={memberships.hasNextPage}
          loading={memberships.isFetchingNextPage}
          onLoadMore={() => void memberships.fetchNextPage()}
        />
      </QueryState>
      {editing === null ? null : (
        <MembershipUpdateDialog
          membership={editing}
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
        />
      )}
    </SheetSection>
  );
}

function Credit({
  organization,
  self,
}: {
  readonly organization: Organization;
  readonly self: AdminSelf;
}) {
  const exposure = organization.dedicatedProgramExposure;
  return (
    <>
      <DetailGrid
        items={[
          ...(exposure === undefined
            ? []
            : [
                {
                  key: 'cap',
                  label: 'سقف تعهد برنامه‌ها',
                  value: formatToman(exposure.totalExposureCapIrr),
                },
                {
                  key: 'committed',
                  label: 'تخصیص فعال',
                  value: formatToman(exposure.committedIrr),
                },
                {
                  key: 'programs',
                  label: 'برنامهٔ اختصاصی فعال',
                  value: formatCount(exposure.activeProgramCount),
                },
              ]),
          {
            key: 'contact',
            label: 'نماینده',
            value:
              organization.contactNameFa === null && organization.contactMobileMasked === null ? (
                '—'
              ) : (
                <span className="admin-inline">
                  {organization.contactNameFa ?? ''}
                  {organization.contactMobileE164 === undefined ||
                  organization.contactMobileE164 === null ? (
                    organization.contactMobileMasked === null ? null : (
                      <Masked kind="mobile" value={organization.contactMobileMasked} />
                    )
                  ) : (
                    <Ltr>{organization.contactMobileE164}</Ltr>
                  )}
                </span>
              ),
          },
        ]}
      />
      <SheetSection id="organization-roles-used" title="نقش‌های استفاده‌شده">
        {organization.membershipCountsByRole.length === 0 ? (
          <p className="admin-muted">هنوز عضویتی ثبت نشده است.</p>
        ) : (
          <ul className="admin-plain-list">
            {organization.membershipCountsByRole.map((row) => (
              <li
                key={row.role.id}
              >{`${row.role.nameFa}: ${formatCount(row.activeCount)} عضو فعال`}</li>
            ))}
          </ul>
        )}
      </SheetSection>
      <SheetSection
        actions={
          canUse(self, 'createCreditProgram') && organization.status === 'ACTIVE' ? (
            <Link
              className={buttonVariants({ size: 'sm', variant: 'secondary' })}
              to={`/credit-programs/new?dedicatedOrganizationId=${organization.id}`}
            >
              برنامهٔ اختصاصی جدید
            </Link>
          ) : undefined
        }
        id="organization-programs"
        title="برنامه‌های اعتبار اختصاصی"
      >
        {organization.dedicatedCreditPrograms.length === 0 ? (
          <p className="admin-muted">برنامهٔ اختصاصی برای این سازمان تعریف نشده است.</p>
        ) : (
          <Table
            caption="برنامه‌های اعتبار اختصاصی سازمان"
            columns={[
              {
                header: 'برنامه',
                key: 'name',
                pin: 'start',
                render: (row) =>
                  canUse(self, 'listCreditPrograms') ? (
                    <Link to={`/credit-programs/${row.id}`}>{row.nameFa}</Link>
                  ) : (
                    row.nameFa
                  ),
              },
              {
                header: 'کد / نسخه',
                key: 'code',
                render: (row) => (
                  <bdi dir="ltr">{formatProgramCodeRevision(row.programCode, row.revision)}</bdi>
                ),
              },
              {
                header: 'وضعیت',
                key: 'status',
                render: (row) => <StatusChip enumName="creditProgramStatus" value={row.status} />,
              },
              ...(canUse(self, 'previewCreditProgramTargeting')
                ? [
                    {
                      detail: false,
                      header: <span className="saay-visually-hidden">تخصیص به اعضا</span>,
                      key: 'target',
                      pin: 'end' as const,
                      render: (row: Organization['dedicatedCreditPrograms'][number]) =>
                        row.status === 'ACTIVE' ? (
                          <Link
                            className={buttonVariants({ size: 'sm', variant: 'secondary' })}
                            state={OPENED_FROM_LIST}
                            to={creditProgramTargetingPath(row.id, {
                              organizationId: organization.id,
                              reason: 'اعتبار اعضای سازمان',
                            })}
                          >
                            تخصیص به اعضا
                          </Link>
                        ) : null,
                    },
                  ]
                : []),
            ]}
            rowKey={(row) => row.id}
            rows={organization.dedicatedCreditPrograms}
          />
        )}
      </SheetSection>
    </>
  );
}

function Enrollments({
  organization,
  self,
}: {
  readonly organization: Organization;
  readonly self: AdminSelf;
}) {
  const requests = useEnrollmentRequests(organization.id);
  const rows = requests.data?.pages.flatMap((page) => page.items) ?? [];
  const canCreate = organization.status === 'ACTIVE' && canUse(self, 'createEnrollmentRequest');
  const open = (id: string) => `/organizations/${organization.id}/enrollments/${id}`;
  return (
    <SheetSection
      actions={
        canCreate ? (
          <Link
            className={buttonVariants({ size: 'sm' })}
            state={OPENED_FROM_LIST}
            to={`/organizations/${organization.id}/enrollments/new`}
          >
            <Icon name="plus" />
            ثبت‌نام گروهی جدید
          </Link>
        ) : undefined
      }
      id="organization-enrollments"
      title="ثبت‌نام‌ها"
    >
      <QueryState
        empty={<EmptyState icon="doc" title="درخواست ثبت‌نامی ثبت نشده است" />}
        error={requests.error}
        isEmpty={rows.length === 0}
        onRetry={() => void requests.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری درخواست‌ها…" lines={3} />}
        status={listStatus(requests)}
      >
        <Table
          caption="درخواست‌های ثبت‌نام گروهی"
          columns={[
            {
              header: 'مستند',
              key: 'evidence',
              pin: 'start',
              render: (row) => (
                <Link state={OPENED_FROM_LIST} to={open(row.id)}>
                  {row.evidenceReference}
                </Link>
              ),
            },
            {
              header: 'ردیف',
              key: 'rows',
              numeric: true,
              render: (row) => formatCount(row.memberCount),
            },
            {
              header: 'نتیجه',
              key: 'done',
              render: (row) => enrollmentOutcomeText(row.countsByRowStatus),
            },
            {
              header: 'ثبت',
              key: 'created',
              render: (row) => formatTimestamp(row.createdAt, { withTime: false }),
            },
            {
              header: 'وضعیت',
              key: 'status',
              pin: 'end',
              render: (row) => <StatusChip enumName="enrollmentRequestStatus" value={row.status} />,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={requests.hasNextPage}
          loading={requests.isFetchingNextPage}
          onLoadMore={() => void requests.fetchNextPage()}
        />
      </QueryState>
    </SheetSection>
  );
}

export function OrganizationDetailSheet() {
  const { self } = useOutletContext<OrganizationsOutletContext>();
  const { organizationId = '' } = useParams();
  const organization = useOrganization(organizationId);
  const close = useCloseSheet('/organizations');
  const [tab, setTab] = useState<DetailTab>('members');
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const name = organization.data?.displayNameFa ?? 'سازمان';
  usePageTitle(name, true, 'سازمان');
  const data = organization.data;
  const subtitle =
    data === undefined ? undefined : (
      <>
        {organizationTypeLabel(data.organizationType)}
        {` · ${data.city.nameFa}`}
      </>
    );
  return (
    <>
      <DetailHeader
        actions={
          data === undefined ? undefined : (
            <OrganizationHeaderActions
              onAddMember={() => setAddingMember(true)}
              onEdit={() => setEditing(true)}
              organization={data}
              self={self}
            />
          )
        }
        actionsLabel="کارهای سازمان"
        back={{ label: 'بازگشت به فهرست', onClick: close }}
        meta={subtitle}
        status={
          data === undefined ? undefined : (
            <>
              <StatusChip enumName="activeStatus" value={data.status} />
              <Ltr>{data.organizationCode}</Ltr>
            </>
          )
        }
        title={name}
      />
      <QueryState
        error={organization.error}
        onRetry={() => void organization.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری سازمان…" lines={8} />}
        status={organization.status}
      >
        {data === undefined ? null : (
          <>
            {data.status === 'INACTIVE' ? (
              <Banner tone="warning">سازمان غیرفعال است.</Banner>
            ) : null}
            <Tabs
              label="بخش‌های سازمان"
              onChange={setTab}
              panel={
                tab === 'members' ? (
                  <Members
                    onAddMember={() => setAddingMember(true)}
                    organization={data}
                    self={self}
                  />
                ) : tab === 'credit' ? (
                  <Credit organization={data} self={self} />
                ) : tab === 'marketing' ? (
                  <ScopeMarketingAssignments organizationId={data.id} />
                ) : (
                  <Enrollments organization={data} self={self} />
                )
              }
              tabs={[
                { key: 'members', label: 'اعضا' },
                { key: 'credit', label: 'اعتبار' },
                { key: 'enrollments', label: 'ثبت‌نام‌ها' },
                ...(canSeeArea(self, 'marketing')
                  ? [{ key: 'marketing' as const, label: 'بازاریابی' }]
                  : []),
              ]}
              value={tab}
            />
            {editing ? (
              <OrganizationDialog
                editing={data}
                onOpenChange={(next) => {
                  if (!next) setEditing(false);
                }}
              />
            ) : null}
            {addingMember ? (
              <MembershipCreateDialog
                onOpenChange={(next) => {
                  if (!next) setAddingMember(false);
                }}
                organization={data}
              />
            ) : null}
          </>
        )}
      </QueryState>
    </>
  );
}

/** `/organizations/new`: the prototype «تعریف سازمان جدید» sheet. */
export function OrganizationCreateSheet() {
  usePageTitle('تعریف سازمان جدید');
  const close = useCloseSheet('/organizations');
  return <OrganizationDialog onOpenChange={onSheetOpenChange(close)} />;
}
